import { Children, type ReactNode } from 'react';

import { isSlot } from '../../core/utils/is-slot.js';
import { ItemTooltip } from './ItemTooltip.js';
import { Prefix } from './Prefix.js';
import { Suffix } from './Suffix.js';

/**
 * Defines slots into which children of this item should be rendered. There
 * are special slots for the tooltip, prefix, and suffix. Everything else will be
 * rendered into the freeform slot.
 */
type ItemSlots = {
  tooltip: ReactNode | null;
  prefix: ReactNode | null;
  suffix: ReactNode | null;
  freeform: ReactNode[];
};

/**
 * Defines slots for SubTrigger, which supports prefix (but not suffix or tooltip).
 */
type SubTriggerSlots = {
  prefix: ReactNode | null;
  freeform: ReactNode[];
};

/**
 * Iterates over child nodes to extract specific component slots for Menu.Item and Menu.Link.
 */
export function getItemSlots(children: ReactNode) {
  const slots: ItemSlots = {
    tooltip: null,
    prefix: null,
    suffix: null,
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, ItemTooltip)) {
      slots.tooltip = child;
    } else if (isSlot(child, Prefix)) {
      slots.prefix = child;
    } else if (isSlot(child, Suffix)) {
      slots.suffix = child;
    } else {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/**
 * Iterates over child nodes to extract specific component slots for Menu.SubTrigger.
 */
export function getSubTriggerSlots(children: ReactNode) {
  const slots: SubTriggerSlots = {
    prefix: null,
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Prefix)) {
      slots.prefix = child;
    } else if (!isSlot(child, ItemTooltip) && !isSlot(child, Suffix)) {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/**
 * Iterates over child nodes to extract specific component slots for Menu.Intent.
 * Intent has the same slot requirements as SubTrigger (prefix only, no suffix or tooltip).
 */
export const getIntentSlots = getSubTriggerSlots;
